// src/lib/gtm.js
// Centralised Google Tag Manager / dataLayer helper for Hublu.
// All ecommerce events follow the GA4 enhanced-ecommerce schema.

/** Push any object onto the dataLayer (creates it if absent). */
export function pushDataLayer(event) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(event);
}

// ─── Ecommerce helpers ────────────────────────────────────────────────────────

/**
 * Map a Hublu cart item to a GA4 item object.
 * @param {Object} item  - cart item or order item
 * @param {number} [idx] - list position (0-based)
 */
function toGA4Item(item, idx = 0) {
  return {
    item_id:         String(item.id   ?? ''),
    item_name:       item.title ?? item.name ?? '',
    item_category:   item.category_name ?? '',
    price:           Number(item.price ?? 0),
    quantity:        Number(item.quantity ?? 1),
    index:           idx,
    currency:        'ZAR',
  };
}

// ─── Specific events ──────────────────────────────────────────────────────────

/**
 * Fired when a product is added to the cart (from ShopPage / ProductPage).
 * @param {Object} item  - cart item
 */
export function trackAddToCart(item) {
  pushDataLayer({ ecommerce: null }); // clear previous ecommerce data
  pushDataLayer({
    event: 'add_to_cart',
    ecommerce: {
      currency: 'ZAR',
      value:    Number(item.price ?? 0),
      items:    [toGA4Item(item)],
    },
  });
}

/**
 * Fired when a product is removed from the cart.
 * @param {Object} item  - cart item
 */
export function trackRemoveFromCart(item) {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: 'remove_from_cart',
    ecommerce: {
      currency: 'ZAR',
      value:    Number(item.price ?? 0) * Number(item.quantity ?? 1),
      items:    [toGA4Item(item)],
    },
  });
}

/**
 * Fired when the user reaches the checkout page.
 * @param {Array}  items       - cart items
 * @param {number} value       - cart total (subtotal + delivery)
 */
export function trackBeginCheckout(items, value) {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: 'begin_checkout',
    ecommerce: {
      currency: 'ZAR',
      value:    Number(value ?? 0),
      items:    items.map(toGA4Item),
    },
  });
}

/**
 * Fired after a successful payment (PayFast return page or EFT modal close).
 * @param {Object} params
 * @param {string} params.transactionId  - order UUID / short ref
 * @param {number} params.value          - order total
 * @param {number} params.shipping       - delivery cost
 * @param {Array}  params.items          - order items
 */
export function trackPurchase({ transactionId, value, shipping, items }) {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: 'purchase',
    ecommerce: {
      transaction_id: transactionId,
      currency:       'ZAR',
      value:          Number(value    ?? 0),
      shipping:       Number(shipping ?? 0),
      items:          (items ?? []).map(toGA4Item),
    },
  });
}

/**
 * Fired when the user views a product detail page.
 * @param {Object} product
 */
export function trackViewItem(product) {
  pushDataLayer({ ecommerce: null });
  pushDataLayer({
    event: 'view_item',
    ecommerce: {
      currency: 'ZAR',
      value:    Number(product.price ?? 0),
      items:    [toGA4Item(product)],
    },
  });
}